import $, { Cash } from "cash-dom";
import PhotoSwipeLightbox from "photoswipe/lightbox";
import "photoswipe/style.css";
import { onReady, publish, subscribe } from "@js/humans/helper";
import { ModalClosedEvent } from "./types/modal";

/**
 * Lightbox Module
 * Initializes and manages PhotoSwipe lightboxes for main content and modals
 * @package KAA
 * @author Tim Jungmann
 */
export default class Lightbox {
    static #instance: Lightbox;

    protected mainLightbox?: PhotoSwipeLightbox;
    protected modalLightbox: Record<number, PhotoSwipeLightbox> = {};
    protected repositoryLightboxes: Map<string, PhotoSwipeLightbox> = new Map();

    /**
     * Custom icons
     */
    protected closeSVGString =
        '<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" style="--icon-ratio: 1;" class="icon icon--kaan-close" aria-hidden="true"><path d="M4 16L16.0208 3.97919" stroke-width="1.2"></path><path d="M4 4L16.0208 16.0208" stroke-width="1.2"></path></svg>';
    protected leftArrowSVGString =
        '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon--arrow-left" width="1em" height="1em" viewBox="0 0 74 74" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><g transform="translate(-1071.5 -644)"><path d="M-8310.185,6419.4 l35,35 l-35,35" transform="translate(-7201.685 7135.4) rotate(180)"/></g></svg>';
    protected rightArrowSVGString =
        '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon--arrow-right" width="1em" height="1em" viewBox="0 0 74 74" fill="none" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"><g transform="translate(-1071.5 -644)"><path d="M-8310.185,6419.4 l35,35 l-35,35" transform="translate(9418.858 -5773.4)"/></g></svg>';


    protected constructor() {
        this.registerEvents();
    }

    /**
     * Returns the singleton instance
     */
    public static getInstance(): Lightbox {
        if (!Lightbox.#instance) {
            Lightbox.#instance = new Lightbox();
        }
        return Lightbox.#instance;
    }

    /**
     * Register required DOM and PubSub events
     */
    protected registerEvents(): void {
        // Initialize main lightbox when DOM is ready
        onReady(() => {
            this.createMainLightbox();
            this.initPreExpandedRepositories();
        });

        // Re-initialize when main content is updated
        subscribe("main.updated", this.createMainLightbox.bind(this));

        // Initialize lightbox when modal content is loaded
        subscribe("modal.content-loaded", (_topic, payload: { $modal: any, level: number }) => {
            this.createModalLightbox(payload.$modal, payload.level);
        });

        // Clean up when modal is closed
        subscribe("modal.closed", this.onModalClosed.bind(this));

        // Initialize repository lightboxes when repository item is expanded
        subscribe("repository.expanded", (_topic, payload: { $article: any }) => {
            const article = payload.$article?.get ? payload.$article.get(0) : payload.$article;
            this.createRepositoryLightbox(article);
        });
    }

    /**
     * Creates a PhotoSwipe lightbox for a given gallery element
     * @param galleryElement The element containing lightbox items
     * @param options Additional configuration options
     * @returns The created PhotoSwipeLightbox instance
     */
    protected createLightbox(
        galleryElement: HTMLElement | string,
        options: {
            appendToEl?: HTMLElement;
            onBeforeOpen?: () => void;
            onClose?: () => void;
        } = {}
    ): PhotoSwipeLightbox | null {
        // Resolve gallery element
        const gallery = typeof galleryElement === 'string'
            ? document.querySelector(galleryElement) as HTMLElement
            : galleryElement;

        if (!gallery) {
            return null;
        }

        // Get lightbox items
        const lightboxItems = gallery.querySelectorAll('a[data-lightbox]');

        // Check if it should be a carousel
        const lightboxCarousel = gallery.querySelectorAll('a[data-lightbox-carousel]');
        const isCarousel = lightboxItems.length > 1 && lightboxCarousel.length > 0;

        // Create base configuration
        const config: any = {
            zoom: false,
            pswpModule: () => import("photoswipe"),
            bgOpacity: 1,
            indexIndicatorSep: "/",
            showHideAnimationType: "fade",
            gallery,
            children: "a[data-lightbox]",

            // Prevent zooming by always defining 'fit'
            initialZoomLevel: "fit",
            secondaryZoomLevel: "fit",
            maxZoomLevel: "fit",

            // Define whether to show arrows
            arrowPrev: isCarousel,
            arrowNext: isCarousel,
            counter: isCarousel,

            // Define custom icons
            closeSVG: this.closeSVGString,
            arrowPrevSVG: this.leftArrowSVGString,
            arrowNextSVG: this.rightArrowSVGString,

            // Define padding
            paddingFn: (viewportSize: any) => ({
                top: viewportSize.x < 1024 ? 20 : 60,
                bottom: viewportSize.x < 1024 ? 20 : 60,
                left: viewportSize.x < 1024 ? 20 : 60,
                right: viewportSize.x < 1024 ? 20 : 60,
            }),
        };

        // Add optional appendToEl
        if (options.appendToEl) {
            config.appendToEl = options.appendToEl;
        }

        // Create lightbox
        const lightbox = new PhotoSwipeLightbox(config);

        // Add event handlers if provided
        if (options.onBeforeOpen) {
            lightbox.on("beforeOpen", options.onBeforeOpen);
        }

        if (options.onClose) {
            lightbox.on("close", options.onClose);
        }

        lightbox.init();
        return lightbox;
    }

    /**
     * Creates the PhotoSwipe lightbox for the main content
     */
    protected createMainLightbox(): void {
        // Destroy previously created lightbox
        if (this.mainLightbox) {
            this.mainLightbox.destroy();
        }

        // Check if we are inside of a modal
        const isInModal = document.body.classList.contains('has-modal');
        const gallerySelector = isInModal ? "#modal" : '[data-pswp-gallery]';
        const lightboxContainer = isInModal ? "#modal" : "#main";

        const lightbox = this.createLightbox(gallerySelector, {
            appendToEl: document.querySelector(lightboxContainer) as HTMLElement,
        });

        if (lightbox) {
            this.mainLightbox = lightbox;
        }
    }

    /**
     * Creates the PhotoSwipe lightbox for modals, once the modal content is added
     */
    protected createModalLightbox($modal: Cash, level: number): void {
        if (level in this.modalLightbox) {
            this.modalLightbox[level].destroy();
        }

        const modalElement = $modal.get(0) as HTMLElement;

        const lightbox = this.createLightbox(modalElement, {
            onBeforeOpen: () => publish("modal.focustrap.deactivate"),
            onClose: () => publish("modal.focustrap.activate"),
        });

        if (lightbox) {
            this.modalLightbox[level] = lightbox;
        }
    }

    /**
     * Triggered once a modal window is closed
     * We will destroy a potential lightbox for this modal level
     */
    protected onModalClosed(topic: string, payload: ModalClosedEvent): void {
        if (!payload.isAsync || !payload.level) {
            return;
        }

        if (
            payload.level in this.modalLightbox &&
            this.modalLightbox[payload.level].pswp
        ) {
            this.modalLightbox[payload.level].destroy();
        }
    }

    /**
     * Initialize lightboxes for repository items that are already expanded on page load
     */
    protected initPreExpandedRepositories(): void {
        const expandedArticles = document.querySelectorAll('.js-repo-article.is-expanded');
        expandedArticles.forEach((article) => {
            this.createRepositoryLightbox(article as HTMLElement);
        });
    }

    /**
     * Creates a PhotoSwipe lightbox for a specific repository article
     */
    protected createRepositoryLightbox(article: HTMLElement): void {
        // Get the gallery element with data-pswp-gallery attribute
        const galleryElement = article.querySelector('[data-pswp-gallery]') as HTMLElement;

        if (!galleryElement) {
            return;
        }

        const galleryId = galleryElement.getAttribute('data-pswp-gallery');

        if (!galleryId) {
            return;
        }

        // Check if lightbox already exists for this gallery
        if (this.repositoryLightboxes.has(galleryId)) {
            return;
        }

        // Check if there are any lightbox items
        const lightboxItems = galleryElement.querySelectorAll('a[data-lightbox]');

        if (lightboxItems.length === 0) {
            return;
        }

        // Create the lightbox using unified method
        const lightbox = this.createLightbox(galleryElement);
        if (lightbox) {
            this.repositoryLightboxes.set(galleryId, lightbox);
        }
    }
}
